import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Switch } from './ui/switch.jsx';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AutoOptimizeToggle({ clusters = [], selectedClusterId, onClusterChange, onStatusChange }) {
    const [enabled, setEnabled] = useState(false);
    const [status, setStatus] = useState(null);
    const [realtimeData, setRealtimeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [clusterSavings, setClusterSavings] = useState(0);

    const clusterId = selectedClusterId;

    const fetchStatus = useCallback(async () => {
        if (!clusterId) return;
        try {
            const response = await axios.get(`${API}/clusters/${clusterId}/auto-optimize/status`);
            setStatus(response.data);
            setEnabled(response.data.enabled);
            onStatusChange?.(response.data.enabled);
        } catch (error) {
            console.error('Failed to fetch auto-optimize status:', error);
        } finally {
            setLoading(false);
        }
    }, [clusterId, onStatusChange]);

    const fetchRealtimeData = useCallback(async () => {
        if (!clusterId) return;
        try {
            const response = await axios.get(`${API}/clusters/${clusterId}/realtime-graph`);
            setRealtimeData(response.data);
        } catch (error) {
            console.error('Failed to fetch realtime data:', error);
        }
    }, [clusterId]);

    const fetchClusterSavings = useCallback(async () => {
        if (!clusterId) return;
        try {
            const statsRes = await axios.get(`${API}/dashboard/stats?cluster_id=${clusterId}`);
            if (statsRes.data?.realized_savings > 0) {
                setClusterSavings(Math.round(statsRes.data.realized_savings));
                return;
            }
        } catch (error) {
            console.error('Dashboard stats not available:', error);
        }
        setClusterSavings(status?.total_savings || 0);
    }, [clusterId, status?.total_savings]);

    useEffect(() => {
        fetchStatus();
        fetchRealtimeData();
        fetchClusterSavings();
        const interval = setInterval(() => {
            fetchStatus();
            fetchRealtimeData();
            fetchClusterSavings();
        }, 30000);
        return () => clearInterval(interval);
    }, [fetchStatus, fetchRealtimeData, fetchClusterSavings]);

    const handleToggle = async (newValue) => {
        setToggling(true);
        try {
            if (newValue) {
                await axios.put(`${API}/clusters/${clusterId}/auto-optimize/enable`, { interval_minutes: 30 });
                toast.success('Auto-optimization enabled');
            } else {
                await axios.put(`${API}/clusters/${clusterId}/auto-optimize/disable`);
                toast.success('Auto-optimization disabled');
            }
            setEnabled(newValue);
            onStatusChange?.(newValue);
            await fetchStatus();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to toggle auto-optimize');
        } finally {
            setToggling(false);
        }
    };

    // Backend timestamps are UTC instants but may arrive WITHOUT a timezone
    // suffix ("2026-07-04T09:51:00.123"). JS parses offset-less strings as the
    // viewer's LOCAL time, skewing every display by the UTC offset (a fresh
    // run showed "Last 5h ago" in IST). Force UTC when no offset is present.
    const parseUtc = (isoString) => {
        if (!isoString) return null;
        const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(isoString);
        return new Date(hasTz ? isoString : isoString + 'Z');
    };

    const timeAgo = (isoString) => {
        if (!isoString) return '--';
        const d = parseUtc(isoString);
        const now = new Date();
        const m = Math.floor((now - d) / 60000);
        if (m < 1) return 'Just now';
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        return d.toLocaleDateString();
    };

    // Compute time-until-next-run from `last_run + interval_minutes` rather
    // than trusting `status.next_run`. That field can be stale (an old run
    // wrote `next_run` based on a previously-configured interval, then the
    // interval was lowered). Clamp negative values to "due now" and cap
    // anything implausibly large (more than 2× the interval) back to the
    // interval — that's a clear signal the saved next_run is wrong.
    const nextRunLabel = () => {
        const interval = Number(status?.interval_minutes) || 30;
        const lastRunMs = status?.last_run ? parseUtc(status.last_run).getTime() : null;
        const nextRunMs = status?.next_run ? parseUtc(status.next_run).getTime() : null;
        // Prefer last_run + interval when last_run exists; fall back to
        // backend-provided next_run; final fallback "any moment now".
        const computed = lastRunMs ? lastRunMs + interval * 60_000 : nextRunMs;
        if (!computed) return 'any moment';
        let ms = computed - Date.now();
        const maxMs = interval * 60_000 * 2;
        if (ms > maxMs) ms = interval * 60_000; // stale next_run, fall back to interval
        if (ms <= 0) return 'due now';
        const mins = Math.ceil(ms / 60_000);
        if (mins < 60) return `${mins} min`;
        const h = Math.floor(mins / 60);
        const rem = mins % 60;
        return rem ? `${h}h ${rem}m` : `${h}h`;
    };

    const cpuPct = realtimeData ? Math.min(100, (realtimeData.cpu?.usage / realtimeData.cpu?.requests * 100) || 0) : 0;
    const memPct = realtimeData ? Math.min(100, (realtimeData.memory?.usage / realtimeData.memory?.requests * 100) || 0) : 0;
    const cpuSavable = realtimeData?.savings_potential?.cpu_pct?.toFixed(0) || 0;
    const memSavable = realtimeData?.savings_potential?.memory_pct?.toFixed(0) || 0;

    if (loading) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-surface-border bg-white dark:bg-surface-dark p-12 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary mr-2.5" />
                <span className="text-sm text-text-secondary">Loading...</span>
            </div>
        );
    }

    return (
        <div className={`rounded-xl border transition-all duration-300 ${
            enabled
                ? 'border-green-200 dark:border-green-500/20 bg-gradient-to-b from-green-50/80 to-white dark:from-green-950/20 dark:to-surface-dark'
                : 'border-gray-200 dark:border-surface-border bg-white dark:bg-surface-dark'
        }`}>
            {/* Header */}
            <div className="p-6 pb-5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                            enabled
                                ? 'bg-green-100 dark:bg-green-500/15'
                                : 'bg-gray-100 dark:bg-surface-border'
                        }`}>
                            <span className={`material-symbols-outlined text-[24px] ${
                                enabled ? 'text-green-600 dark:text-green-400' : 'text-slate-400'
                            }`}>bolt</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <h3 className="text-lg font-semibold dark:text-white text-slate-900">Auto-Optimization</h3>
                                {enabled && (
                                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-500/15 px-2.5 py-0.5 rounded-full">
                                        <span className="relative flex h-1.5 w-1.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                                        </span>
                                        Running
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-text-secondary mt-1">
                                {enabled
                                    ? `Optimizing every ${status?.interval_minutes || 30} minutes`
                                    : 'Enable to continuously rightsize workloads and reduce cost'
                                }
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 sm:pt-1">
                        {clusters.length > 0 && (
                            <select
                                value={clusterId || ''}
                                onChange={(e) => onClusterChange?.(e.target.value)}
                                className="text-sm border border-gray-200 dark:border-surface-border bg-white dark:bg-background-dark rounded-lg px-3 py-2 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                            >
                                {clusters.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        )}
                        <div
                            title={enabled
                                ? `${status?.total_runs || 0} cycles completed · $${clusterSavings.toLocaleString()} saved`
                                : 'Enable to start optimizing'}
                        >
                            {toggling ? (
                                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                            ) : (
                                <Switch checked={enabled} onCheckedChange={handleToggle} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Metrics */}
            <div className="px-6 pb-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Savings */}
                    <div className={`rounded-xl p-5 ${
                        enabled
                            ? 'bg-green-100/60 dark:bg-green-500/10 ring-1 ring-green-200/60 dark:ring-green-500/10'
                            : 'bg-gray-50 dark:bg-background-dark ring-1 ring-gray-100 dark:ring-surface-border'
                    }`}>
                        <div className="flex items-center gap-1.5 mb-3">
                            <span className={`material-symbols-outlined text-[16px] ${
                                enabled ? 'text-green-600 dark:text-green-400' : 'text-slate-400'
                            }`}>savings</span>
                            <p className="text-xs font-medium text-text-secondary">Realized Savings</p>
                        </div>
                        <p className={`text-3xl font-bold tracking-tight ${
                            enabled ? 'text-green-700 dark:text-green-400' : 'text-slate-900 dark:text-white'
                        }`}>
                            ${clusterSavings.toLocaleString()}
                        </p>
                        <p className="text-xs text-text-secondary mt-1.5">month to date</p>
                    </div>

                    {/* Cycles */}
                    <div className="rounded-xl p-5 bg-gray-50 dark:bg-background-dark ring-1 ring-gray-100 dark:ring-surface-border">
                        <div className="flex items-center gap-1.5 mb-3">
                            <span className="material-symbols-outlined text-[16px] text-slate-400">cycle</span>
                            <p className="text-xs font-medium text-text-secondary">Cycles</p>
                        </div>
                        <p className="text-3xl font-bold dark:text-white text-slate-900 tracking-tight">{status?.total_runs || 0}</p>
                        <p className="text-xs text-text-secondary mt-1.5">
                            {status?.last_run ? `Last ${timeAgo(status.last_run)}` : 'No runs yet'}
                        </p>
                    </div>

                    {/* CPU */}
                    <div className="rounded-xl p-5 bg-gray-50 dark:bg-background-dark ring-1 ring-gray-100 dark:ring-surface-border">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[16px] text-slate-400">memory</span>
                                <p className="text-xs font-medium text-text-secondary">CPU</p>
                            </div>
                            {Number(cpuSavable) > 0 && (
                                <span className="text-[11px] font-semibold text-green-600 dark:text-green-400">{cpuSavable}% savable</span>
                            )}
                        </div>
                        <p className="text-3xl font-bold dark:text-white text-slate-900 tracking-tight">{Math.round(cpuPct)}%</p>
                        <div className="mt-2.5 h-1.5 bg-gray-200 dark:bg-surface-border rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${cpuPct}%` }} />
                        </div>
                        {realtimeData?.cpu && (
                            <p className="text-xs text-text-secondary mt-1.5">
                                {realtimeData.cpu.usage?.toFixed(1)} / {realtimeData.cpu.requests?.toFixed(1)} cores
                            </p>
                        )}
                    </div>

                    {/* Memory */}
                    <div className="rounded-xl p-5 bg-gray-50 dark:bg-background-dark ring-1 ring-gray-100 dark:ring-surface-border">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-[16px] text-slate-400">database</span>
                                <p className="text-xs font-medium text-text-secondary">Memory</p>
                            </div>
                            {Number(memSavable) > 0 && (
                                <span className="text-[11px] font-semibold text-green-600 dark:text-green-400">{memSavable}% savable</span>
                            )}
                        </div>
                        <p className="text-3xl font-bold dark:text-white text-slate-900 tracking-tight">{Math.round(memPct)}%</p>
                        <div className="mt-2.5 h-1.5 bg-gray-200 dark:bg-surface-border rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full transition-all duration-500" style={{ width: `${memPct}%` }} />
                        </div>
                        {realtimeData?.memory && (
                            <p className="text-xs text-text-secondary mt-1.5">
                                {realtimeData.memory.usage?.toFixed(1)} / {realtimeData.memory.requests?.toFixed(1)} GB
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer — cadence + next run.
                Cadence comes from `status.interval_minutes` (config-driven,
                default 15). "Next in" is derived from last_run + interval so
                stale `next_run` values (set by an earlier config) can't show
                a misleading "in 5h". */}
            {enabled && (
                <div className="px-6 py-3 border-t border-green-100 dark:border-green-500/10 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[14px] text-text-secondary">schedule</span>
                    <p className="text-xs text-text-secondary">
                        Cycle: every <span className="font-medium dark:text-white text-slate-700">{Number(status?.interval_minutes) || 30} min</span>
                        <span className="mx-1.5">·</span>
                        next in <span className="font-medium dark:text-white text-slate-700">{nextRunLabel()}</span>
                    </p>
                </div>
            )}
        </div>
    );
}
